import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { updateUserPrefEvent, UserPrefs } from "@/stores/userPrefsDomain";
import { logDebug, logError } from "@/utils/log";
import { fromQueryString, QueryParams, singleValue } from "@/utils/queryParams";
import { uncapitalize } from "@/utils/strings";
import { capitalize, identity, isNil, omit, partial } from "lodash";
import {
  DEFAULT_LANG,
  isSupportedLanguage,
  Language,
  toSupportedLangOrUndefined,
} from "./languages";
import { createLocaleFormat, LocaleFormat } from "./localeFormat";
import en from "./locales/en/translation.json";
import no from "./locales/no/translation.json";
import sv from "./locales/sv/translation.json";
import {
  isTMessage,
  isTMessageParam,
  isTranslatedMessage,
  TFunction,
  TMessage,
  TMessageParam,
  TMessageParams,
} from "./tt";
import { TMessageKey } from "./types.gen";

type I18nBundle = { [key: string]: string };

export type I18nState = {
  bundles: { [lang: string]: I18nBundle };
  lang: Language;
  cimode?: boolean;
};

export type PreppedI18nState = {
  lang: Language;
  t: TFunction;
  f: LocaleFormat;
  tf: (val: TMessage | TMessageParam | string | number | undefined | null | false) => string;
};

export type TranslationAdminState = {
  auto?: boolean;
  en?: string;
  no?: string;
  sv?: string;
  translating?: { [lang: string]: boolean };
  addDialog?: { input: string; error?: string };
  renameDialog?: { input: string; error?: string };
};

export const lookupLanguage = ({
  queryParams,
  userPrefs,
  browserLangs,
}: {
  queryParams: QueryParams | undefined;
  userPrefs: UserPrefs | undefined;
  browserLangs: string[] | undefined;
}): Language => {
  const browserLang = browserLangs?.map(toSupportedLangOrUndefined)?.find(identity);
  const storedLang = userPrefs?.lang;
  const queryLang = toSupportedLangOrUndefined(singleValue(queryParams?.lang));
  return queryLang ?? storedLang ?? browserLang ?? DEFAULT_LANG;
};

export function mapMessageParam<Key extends string = string>(
  state: I18nState,
  f: LocaleFormat,
  v: TMessageParams[Key],
): string | number {
  if (isNil(v)) {
    return "";
  }
  if (Array.isArray(v)) {
    return f.formatList(v.map((vv) => mapMessageParam(state, f, vv)));
  }
  switch (typeof v) {
    case "string":
    case "number":
      return v;
    case "object":
      if (isTMessage(v)) {
        return t(state, v);
      } else {
        switch (v.type) {
          case "amount":
            if (!v.currency) {
              return f.formatAmountNumber(v.value);
            } else {
              return f.formatAmount(v.currency, v.value);
            }
          case "capitalize":
            if (typeof v.value === "string") {
              return capitalize(v.value);
            } else if (isTMessage(v.value)) {
              return capitalize(t(state, v.value));
            } else {
              return capitalize(String(mapMessageParam(state, f, v.value)));
            }
          case "country":
            return f.formatCountry(v.code);
          case "date":
            return f.formatDate(v.value, v);
          case "datetime":
            return f.formatDateTime(v.value, v);
          case "exactAmount":
            return f.formatExactAmount({ currency: v.currency, value: v.value });
          case "list":
            return f.formatList(
              v.value.map((vv) => mapMessageParam(state, f, vv)),
              omit(v, ["type", "value"]),
            );
          case "md-list":
            return v.value
              .map((vv) => mapMessageParam(state, f, vv))
              .map((s) => `- ${s}\n`)
              .join("");
          case "nominal-value":
            return f.formatNominalValue(v.currency, v.value);
          case "number":
            return f.formatNumber(v.value, v);
          case "org-no":
            return f.formatOrgNo(v.value);
          case "percent":
            return f.formatPercent(v.value, v);
          case "price":
            return f.formatPrice(v.currency, v.value);
          case "time":
            return f.formatTime(v.value, v);
          case "shareType":
            return f.formatShareType(v.value, v.options);
          case "ssn":
            return f.formatSsn(v.value, v.options);
          case "text":
            return format(state, f, v.value, v.params);
          case "uncapitilize":
            if (isTMessage(v.value)) {
              return uncapitalize(String(t(state, v.value)));
            } else if (isTMessageParam(v.value)) {
              return uncapitalize(String(mapMessageParam(state, f, v.value)));
            } else {
              return uncapitalize(v.value);
            }
          case "weekday":
            return f.formatWeekday(v.value);
          case "year":
            return f.formatYear(v.value);
          case "year-month":
            return f.formatYearMonth(v.value);
        }
      }
  }
}

const lookupMessage = (
  state: I18nState,
  lang: Language,
  key: string,
  params?: TMessageParams,
): string | undefined => {
  if (state.cimode) {
    return key;
  }
  const defaultBundle = state.bundles[DEFAULT_LANG];
  const bundle = state.bundles[lang] ?? defaultBundle;
  const lookup = (key: string) => bundle[key] ?? defaultBundle[key];
  const msg = lookup(key);
  if (msg) {
    return msg;
  } else {
    switch (params?.count) {
      case 0:
        return lookup(`${key}_zero`) ?? lookup(`${key}_other`);
      case 1:
        return lookup(`${key}_one`);
      case 2:
        return lookup(`${key}_two`) ?? lookup(`${key}_other`);
      default:
        return lookup(`${key}_other`);
    }
  }
};

const format = (
  state: I18nState,
  f: LocaleFormat,
  msg: string,
  params?: TMessageParams,
): string => {
  if (!params) {
    return msg;
  } else {
    const formattedParams = Object.entries(params).map(([k, v]) => [
      k,
      mapMessageParam(state, f, v),
    ]);
    return formattedParams.reduce(
      (acc, [k, v]) => acc.replaceAll(`{{${k}}}`, () => String(v)),
      msg,
    );
  }
};

export const t = (
  state: I18nState,
  key: TMessageKey | TMessage | TMessageParam,
  params?: TMessageParams,
): string => {
  if (isTranslatedMessage(key)) {
    return key.translated;
  }

  const lang = isSupportedLanguage(params?.lang)
    ? params.lang
    : isTMessage(key) && isSupportedLanguage(key.params?.lang)
      ? key.params.lang
      : state.lang;
  const defaultBundle = state.bundles[DEFAULT_LANG];
  const bundle = state.bundles[lang] ?? defaultBundle;
  const f = createLocaleFormat(lang);

  if (isTMessageParam(key)) {
    return String(mapMessageParam(state, f, key));
  }

  let k: string;
  let p: TMessageParams | undefined;
  if (typeof key === "string") {
    k = key;
    p = params;
  } else {
    k = key.key;
    p = key.params;
  }
  if (!bundle) {
    return k;
  }

  const msg = lookupMessage(state, lang, k, p);
  if (msg) {
    return format(state, f, msg, p);
  } else {
    if (import.meta.hot) {
      logDebug(
        `Missing translation ${k}. Click the link to add:\nhttp://localhost:4000/t?add=${k}`,
      );
    } else {
      logError(`Missing translation "${k}" for language "${lang}"`, { lang, key, params });
    }
    return k;
  }
};

export const tf = (
  state: I18nState,
  k: TMessage | TMessageParam | string | number | undefined | null | false,
): string => {
  if (typeof k === "string" || typeof k === "number") {
    return String(k);
  } else if (k === null || k === undefined || k === false) {
    return "";
  }
  return t(state, k);
};

export const initialI18nState = (): I18nState => ({
  bundles: { en, no, sv },
  lang: DEFAULT_LANG,
});

export const initI18n = ({
  dispatch,
  userPrefs,
}: {
  dispatch: EventDispatcher;
  userPrefs: UserPrefs;
}) => {
  dispatch([
    {
      type: "update-store",
      data: {
        i18n: {
          bundles: { en, no, sv },
          lang: lookupLanguage({
            queryParams: fromQueryString(location.search),
            browserLangs: Array.from(navigator.languages),
            userPrefs,
          }),
          cimode: fromQueryString(location.search)?.lang === "cimode",
        },
      },
    },
  ]);

  if (import.meta.hot) {
    import.meta.hot.on(
      "i18n/hot-update",
      ({ lang, content }: { lang: string; content: object }) => {
        logDebug(`i18n: hot-update ${lang}`);
        dispatch([
          {
            type: "update-store",
            data: {
              i18n: (cur) => ({
                ...cur,
                bundles: { ...cur.bundles, [lang]: content as I18nBundle },
              }),
            },
          },
        ]);
      },
    );
  }
};

export const changeLanguageAction = (lang: Language): DCEvent[] => [
  { type: "update-store", data: { i18n: (state) => ({ ...state, lang }) } },
  ...updateUserPrefEvent("lang", lang),
];

export const toggleCIMode = (enabled?: boolean): DCEvent[] => [
  {
    type: "update-store",
    data: { i18n: (state) => ({ ...state, cimode: enabled ?? !state.cimode }) },
  },
];

export const prepI18nState = (state: I18nState, lang: Language = state.lang): PreppedI18nState => {
  return {
    lang: lang,
    f: createLocaleFormat(lang),
    t: partial(t, { ...state, lang }),
    tf: partial(tf, { ...state, lang }),
  };
};
